// DICOM files are POSTed through the backend (for anonymization), so each upload
// request is subject to Cloud Run's 32 MiB HTTP/1 request limit (returns 413).
// Batching by total bytes — rather than a fixed file count — keeps each request
// under the limit for large-image modalities (mammography, X-ray, multi-frame).
// See issue #654.
export const MAX_BATCH_BYTES = 25 * 1024 * 1024; // headroom under Cloud Run's 32 MiB
export const MAX_BATCH_FILES = 10; // secondary guard against too many files per request

/**
 * Group files into batches bounded by total bytes AND file count.
 * A file larger than maxBytes is sent on its own (a batch of one).
 *
 * @param {Array<{size: number}>} files - files to batch (browser File objects)
 * @param {object} [limits]
 * @param {number} [limits.maxBytes=MAX_BATCH_BYTES] - max total bytes per batch
 * @param {number} [limits.maxFiles=MAX_BATCH_FILES] - max files per batch
 * @returns {Array<Array>} array of batches, in input order
 */
export function buildBatches(
  files,
  { maxBytes = MAX_BATCH_BYTES, maxFiles = MAX_BATCH_FILES } = {}
) {
  const batches = [];
  let current = [];
  let currentBytes = 0;

  for (const file of files) {
    const size = file.size || 0;
    const tooManyFiles = current.length >= maxFiles;
    // Only split on bytes once the batch is non-empty, so a single oversized
    // file still gets sent (on its own) rather than being dropped.
    const tooManyBytes = current.length > 0 && currentBytes + size > maxBytes;

    if (tooManyFiles || tooManyBytes) {
      batches.push(current);
      current = [];
      currentBytes = 0;
    }

    current.push(file);
    currentBytes += size;
  }

  if (current.length) {
    batches.push(current);
  }

  return batches;
}
